// @flow
/* global window */

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { selectUserCountryCode } from '@gandi/authentication';
import {
  selectCurrency as selectPrepaidCurrency,
  fetchLastPrepaid as fetchPrepaid,
  prepaidResource,
  fetchCurrencies as fetchBUCurrencies,
  selectShouldFetchCurrencies as selectShouldFetchBUCurrencies,
} from '@gandi/billing';
import {
  selectCartUUID,
  fetchCartOwnerList,
  getCart,
  updateCart,
} from '@gandi/cart';
import {
  fetchOneOrganization as fetchOrganization,
  fetchOrganizationsList as fetchOrganizations,
  organizationsResource,
  selectIsLoadedOneOrganization,
} from '@gandi/redux-organization';
import {
  fetchCountry,
  fetchCountryList,
  resource as placesResource,
  selectIsLoadedCountry,
} from '@gandi/redux-places';
import { selectCurrentId, setCurrentId } from '@gandi/redux-sharing';
import { localeSelector, useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@gandi/ui';

import LayoutContext from 'src/components/LayoutContext';
import { useCreateHref, useQuery } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectShopCurrencies } from 'src/modules/app/selectors/currency';

import { type Option, relocalizeUrl } from 'src/modules/app/selectors/locale';
import type { PreFetchArgs, ComponentWithPrefetch, FullState } from 'src/types';
import type { AuthenticatedPreferencesWidgetQuery as Query } from 'src/types/query';

import {
  selectIsFreshUser,
  selectCountry,
  selectCurrency,
} from '../../modules/selectors';

import CountrySelect from './CountrySelect';
import CurrencySelect from './CurrencySelect';
import Fieldset from './Fieldset';
import LinkToAdminOrga from './LinkToAdminOrga';
import LocaleSelect from './LocaleSelect';
import OrgaSelect from './OrgaSelect';
import { FETCH_ORGANIZATIONS_PARAMS } from './modules';
import { selectOrgBU, selectOrgCountry } from './modules/selectors';
import type { DialogState } from './types';

// eslint-disable-next-line import/order
import styles from './styles/common.scss';

export type Props = {
  dialogState: DialogState,
};

function AuthenticatedDialog({ dialogState }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const cartId = useAppSelector(selectCartUUID);
  const createHref = useCreateHref();
  const [searchParams] = useSearchParams();

  // locale
  const defaultLocale = useAppSelector(localeSelector);
  const [locale, setLocale] = React.useState(defaultLocale);

  const localeSelectOnChange = React.useCallback(
    // $FlowFixMe[incompatible-call]
    (option: Option) => setLocale(option.value),
    [],
  );
  // orga
  const { locked } = React.useContext(LayoutContext);
  const currentId = useAppSelector(selectCurrentId);
  const {
    org: defaultOrgId = currentId,
    country: locationQueryCountry,
    currency: locationQueryCurrency,
  } = useQuery<Query>();
  const [orgId, setOrga] = React.useState(defaultOrgId);

  const shouldFetchOrg = useAppSelector(state =>
    organizationsResource.selectShouldFetchOne(state, orgId),
  );
  const isLoadedOrg = useAppSelector(state =>
    selectIsLoadedOneOrganization(state, orgId),
  );

  const orgaSelectOnChange = React.useCallback(
    (option: Option) => setOrga(option.value),
    [],
  );

  React.useEffect(() => {
    if (shouldFetchOrg) {
      dispatch(fetchOrganization(orgId));
    }
  }, [dispatch, orgId, shouldFetchOrg]);

  React.useEffect(() => {
    if (cartId) {
      dispatch(fetchCartOwnerList(cartId));
    } else {
      dispatch(fetchOrganizations(FETCH_ORGANIZATIONS_PARAMS));
    }
  }, [cartId, dispatch]);

  const sharingIdProps = React.useMemo(
    () => ({
      sharingId: orgId,
    }),
    [orgId],
  );
  // prepaid selectors
  const prepaidCurrency = useAppSelector(state =>
    selectPrepaidCurrency(state, sharingIdProps),
  );

  const isLoadedPrepaid = useAppSelector(state =>
    prepaidResource.selectIsLoadedOne(state, orgId),
  );
  const shouldFetchPrepaid = useAppSelector(state =>
    prepaidResource.selectShouldFetchOne(state, orgId),
  );

  const countryProps = React.useMemo(
    () => ({
      locationQueryCountry,
      orgId,
    }),
    [locationQueryCountry, orgId],
  );

  // country
  const defaultCountryCode = useAppSelector(state =>
    selectCountry(state, countryProps),
  );

  const [country, setCountry] = React.useState(defaultCountryCode);
  const countrySelectOnChange = React.useCallback((option: Option) => {
    setCountry(option.value);
  }, []);

  const shouldFetchCountry = useAppSelector(state =>
    placesResource.selectShouldFetchOne(state, country),
  );
  const isLoadedCountry = useAppSelector(state =>
    selectIsLoadedCountry(state, country),
  );
  React.useEffect(() => {
    if (shouldFetchCountry) {
      dispatch(fetchCountry(country));
    }
  }, [dispatch, country, shouldFetchCountry]);

  const shouldFetchCountryList = useAppSelector(
    placesResource.selectShouldFetch,
  );
  React.useEffect(() => {
    if (shouldFetchCountryList) {
      dispatch(fetchCountryList());
    }
  }, [dispatch, country, shouldFetchCountryList]);

  // orga BU
  const orgBU = useAppSelector(state => selectOrgBU(state, orgId));
  const needsBUAsk =
    isLoadedOrg && !!orgBU && isLoadedPrepaid && !prepaidCurrency;
  const needCountrySelect = useAppSelector(state =>
    selectIsFreshUser(state, orgId),
  );
  const needsPrepaid = isLoadedOrg && !!orgBU;
  const isPendingPrepaid = needsPrepaid && shouldFetchPrepaid;

  // prepaid useEffect
  React.useEffect(() => {
    if (isPendingPrepaid) {
      dispatch(fetchPrepaid(orgId));
    }
  }, [dispatch, isPendingPrepaid, orgId]);

  const shopCurrenciesProps = React.useMemo(
    () => ({ country, sharingId: orgId }),
    [country, orgId],
  );

  // currency
  const currencies = useAppSelector(state =>
    selectShopCurrencies(state, shopCurrenciesProps),
  );

  const currencyProps = React.useMemo(
    () => ({
      locationQueryCurrency,
      country,
      orgId,
    }),
    [locationQueryCurrency, country, orgId],
  );

  const defaultCurrency = useAppSelector(state =>
    selectCurrency(state, currencyProps),
  );
  const [currency, setCurrency] = React.useState(defaultCurrency);

  const currencySelectOnChange = React.useCallback(
    (option: Option) => setCurrency(option.value),
    [],
  );

  React.useEffect(() => {
    // check if  the current currency is available from the currency list
    // when this currency list changed
    if (isLoadedCountry) {
      const isCurrencyAvailable = !!currencies.find(c => c === currency);
      if (!isCurrencyAvailable) {
        setCurrency(currencies[0]);
      }
    }
  }, [isLoadedCountry, currencies, currency]);

  React.useEffect(() => {
    // change the current currency when the prepaidCurrency changed
    // (this happens when user select another organization)
    if (prepaidCurrency) {
      setCurrency(prepaidCurrency);
    }
  }, [prepaidCurrency]);

  const shouldFetchBUCurrencies = useAppSelector(state =>
    selectShouldFetchBUCurrencies(state, shopCurrenciesProps),
  );
  const isPendingBUCurrencies = needsBUAsk && shouldFetchBUCurrencies;
  React.useEffect(() => {
    if (isPendingBUCurrencies) {
      dispatch(fetchBUCurrencies(shopCurrenciesProps));
    }
  }, [dispatch, isPendingBUCurrencies, shopCurrenciesProps]);

  // submitting
  const [submitting, setSubmitting] = React.useState(false);
  const handleSubmit: (evt: SyntheticEvent<>) => Promise<void> = async (
    evt: SyntheticEvent<>,
  ) => {
    evt.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    if (cartId) {
      if (
        defaultCurrency !== currency ||
        defaultCountryCode !== country ||
        defaultOrgId !== orgId
      ) {
        try {
          await dispatch(setCurrentId(orgId));
          await dispatch(
            updateCart(
              cartId,
              // updateCart should accept any cart props instead of only products one
              {
                owner_uuid: orgId,
                ...(needCountrySelect && { country, currency }),
              },
              // $FlowFixMe[incompatible-call]
              { headers: needCountrySelect ? { 'X-Dry-Run': 0 } : {} },
            ),
          );
          await dispatch(getCart());
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
          // pass
        }
      }
    } else {
      // if no cart, we have to use the querystring to set the preferences
      searchParams.set('country', country);
      searchParams.set('currency', currency);
    }

    const newLocation: Partial<Location> = {
      search: searchParams.toString(),
    };

    let url = createHref(newLocation);

    // apply the locale like it is done with the bottom locale select
    if (defaultLocale !== locale) {
      url = relocalizeUrl(url, defaultLocale, locale);
    }

    if (defaultOrgId !== orgId) {
      url = relocalizeUrl(url, defaultOrgId, orgId);
    }

    window.location.href = url;
  };

  const orgaFieldset = (
    <Fieldset
      column={!needCountrySelect}
      inputName="orgaSelect"
      label={__('Prices for')}
    >
      <OrgaSelect
        onChange={orgaSelectOnChange}
        disabled={submitting}
        readOnly={locked}
        value={orgId}
        name="orgaSelect"
      />
    </Fieldset>
  );

  return (
    <Dialog {...dialogState} scroll="body">
      <DialogHeader>{__('Preferences')}</DialogHeader>
      <DialogContent>
        <Fieldset inputName="localeSelect" label={__('Change language')}>
          <LocaleSelect
            onChange={localeSelectOnChange}
            disabled={submitting}
            value={locale}
            name="localeSelect"
          />
        </Fieldset>
        {needCountrySelect && orgaFieldset}
        <Base className={styles.columns}>
          {needCountrySelect ? (
            <Fieldset column inputName="countrySelect" label={__('Country')}>
              <CountrySelect
                onChange={countrySelectOnChange}
                disabled={submitting}
                loading={shouldFetchCountryList}
                value={country}
                readOnly={locked}
                name="countrySelect"
              />
            </Fieldset>
          ) : (
            orgaFieldset
          )}
          <Fieldset column inputName="currencySelect" label={__('Currency')}>
            <CurrencySelect
              onChange={currencySelectOnChange}
              country={country}
              disabled={submitting}
              orgId={orgId}
              loading={
                shouldFetchCountry ||
                shouldFetchOrg ||
                isPendingPrepaid ||
                isPendingBUCurrencies
              }
              value={currency}
              name="currencySelect"
            />
            <LinkToAdminOrga orgId={orgId} />
          </Fieldset>
        </Base>
      </DialogContent>
      <DialogFooter>
        <Button
          type="button"
          kind="outline"
          onClick={dialogState.close}
          disabled={submitting}
          small
        >
          {__('Cancel')}
        </Button>
        <Button
          type="button"
          disabled={submitting}
          loading={submitting}
          onClick={handleSubmit}
          small
        >
          {__('Save')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

AuthenticatedDialog.preFetch = async function preFetch(
  args: PreFetchArgs<{}, void, Query, FullState>,
): Promise<any> {
  const { getState, dispatch, query } = args;

  const orgId = query.org || selectCurrentId(getState());
  await Promise.allSettled([
    dispatch(fetchOrganization(orgId)),
    dispatch(fetchPrepaid(orgId)),
  ]);

  const cartId = selectCartUUID(getState());

  const country =
    query.country ||
    selectOrgCountry(getState(), orgId) ||
    selectUserCountryCode(getState());

  return Promise.allSettled([
    dispatch(
      cartId
        ? fetchCartOwnerList(cartId)
        : fetchOrganizations(FETCH_ORGANIZATIONS_PARAMS),
    ),
    dispatch(fetchBUCurrencies({ sharingId: orgId, country })),
    dispatch(fetchCountryList()),
    dispatch(fetchCountry(country)),
  ]);
};

export default (AuthenticatedDialog: ComponentWithPrefetch<
  Props,
  typeof AuthenticatedDialog.preFetch,
>);
